import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import { Chip, Icon, Money, Select } from "../../components/ui";
import { useEntitlement } from "../../contexts";
import { baseCurrencyOptions } from "../../core/currency";
import { getFileUrl } from "../../core/config/api.js";
import { fieldsFor, useRequestLookbook } from "./useRequestLookbook";

/**
 * Minor units in, major units in the box. Every money column on a request
 * stores pence (see `formatMoney(pence)` in core/currency), but nobody types
 * pence — so the input works in pounds and converts on the way out.
 */
const toMajor = (minor) => {
  if (minor === null || minor === undefined || minor === "") return "";
  const n = Number(minor);
  if (!Number.isFinite(n) || n === 0) return "";

  // Whole amounts lose the ".00" — a rep typing 100000 should not find
  // "100000.00" sitting in the box afterwards.
  const major = n / 100;
  return Number.isInteger(major) ? String(major) : major.toFixed(2);
};

const toMinor = (major) => {
  const trimmed = String(major ?? "").trim();
  if (trimmed === "") return "";
  const n = Number(trimmed.replace(/,/g, ""));
  if (!Number.isFinite(n) || n < 0) return null;

  return String(Math.round(n * 100));
};

const isEmptyAmount = (v) =>
  v === null || v === undefined || v === "" || Number(v) === 0;

const monthYear = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", { month: "short", year: "numeric" })
    : null;

/**
 * One money input with its own currency dropdown.
 *
 * The dropdown is per field by request, but all four fields on a request write
 * the SAME `requests.currency` column — that is the agreed behaviour, not an
 * oversight. Changing one therefore changes the others, and the hint under the
 * row says so rather than letting it look like a bug.
 *
 * `suggested`: the value shown is a recommendation, not yet saved — it is
 * styled as such and saved on PROCEED unless the rep changes or clears it.
 */
const AmountField = ({ label, value, suggested, currency, currencies, disabled, onCommit }) => {
  const [draft, setDraft] = useState(() => toMajor(value));
  const lastCommitted = useRef(toMajor(value));

  // Re-sync when the server normalises the value under us (it turns "" and "0"
  // into null), or a suggestion arrives — but never while the field is being
  // typed into.
  useEffect(() => {
    const next = toMajor(value);
    if (next !== lastCommitted.current) {
      lastCommitted.current = next;
      setDraft(next);
    }
  }, [value]);

  const commit = () => {
    if (draft === lastCommitted.current) return;
    const minor = toMinor(draft);
    if (minor === null) {
      // Not a number — put the last good value back rather than saving junk.
      setDraft(lastCommitted.current);
      return;
    }
    lastCommitted.current = draft;
    onCommit({ amount: minor });
  };

  return (
    <label className={`rlb-amount-field${suggested ? " rlb-amount-field--suggested" : ""}`}>
      <span className="rlb-amount-field__label">
        {label}
        {suggested && <span className="rlb-amount-field__tag">Suggested</span>}
      </span>
      <span className="rlb-amount-field__controls">
        <Select
          className="rlb-amount-field__currency"
          size="lg"
          searchable={false}
          value={currency ?? "gbp"}
          disabled={disabled}
          options={currencies}
          onChange={(v) => onCommit({ currency: v })}
          aria-label={`${label} currency`}
        />
        <input
          className="rlb-amount-field__input"
          type="text"
          inputMode="decimal"
          value={draft}
          disabled={disabled}
          placeholder="—"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
        />
      </span>
    </label>
  );
};

/** One line of the recommendations column: a label and its figure(s). */
const Rec = ({ label, children }) => (
  <div className="rlb-rec">
    <span className="rlb-rec__label">{label}</span>
    <div className="rlb-rec__values">{children}</div>
  </div>
);

/**
 * Suggestions for an ORDER request, fetched once per request (and again when
 * its currency changes — every figure comes back in that currency).
 * Sell requests get none; the endpoint answers empty for them.
 */
const useRecommendations = (request) => {
  const [recs, setRecs] = useState(null);
  const isOrder = request?.request_type !== "sell";

  useEffect(() => {
    if (!isOrder || !request?.id) return undefined;

    let alive = true;
    axios
      .get(`/api/requests/${request.id}/lookbook-recommendations`, {
        metadata: { quiet: true },
      })
      .then(({ data }) => alive && setRecs(data))
      // A missing suggestion is not an error the rep can act on — the fields
      // still work exactly as before.
      .catch(() => alive && setRecs(null));

    return () => {
      alive = false;
    };
  }, [isOrder, request?.id, request?.currency]);

  return recs;
};

/**
 * Which listing prefills SELL TARGET: the one matching the request's year, or
 * failing that the newest (the endpoint returns newest first).
 */
const pickListing = (listings, year) => {
  if (!listings?.length) return null;
  const y = String(year ?? "").replace(/\D/g, "").slice(0, 4);

  return listings.find((l) => y && String(l.year) === y) ?? listings[0];
};

/**
 * One request in the amounts dialog: the listing-card look (photo well, brand /
 * model / reference block) with this request's two money fields under it, one
 * above the other, each with its recommendations to the right.
 *
 * Not the shared `ListingCard`: that component hard-codes an ON/OFF and a RSVD
 * toggle onto the photo and a marketplaces footer beneath it, none of which
 * exist for a request. Showing a rep a dead "RSVD" switch on a sourcing enquiry
 * would be worse than the small amount of duplicated styling here, so this
 * borrows the frame and the typography and leaves the listing machinery out.
 */
export const RequestAmountCard = ({ request, position, saveState, onSave }) => {
  const { setPrefill, pendingPrefill } = useRequestLookbook();
  // PriceIQ is the Intelligence add-on — no add-on, no PriceIQ suggestion.
  const hasPriceIQ = useEntitlement("priceiq_quotes");
  const currencies = useMemo(
    () => baseCurrencyOptions().map((o) => ({ value: o.id, label: o.name })),
    []
  );
  const fields = fieldsFor(request);
  const recs = useRecommendations(request);
  const recCurrency = recs?.currency ?? request?.currency;

  // BUY BUDGET ← Buy PriceIQ; SELL TARGET ← a same-reference listing's price.
  // Kept in the hook, not written, until PROCEED.
  useEffect(() => {
    if (!recs || !request?.id) return;
    setPrefill(request.id, {
      buy_budget: hasPriceIQ ? recs.buy_priceiq?.value ?? null : null,
      max_budget: pickListing(recs.listings, request.year)?.price ?? null,
    });
  }, [recs, hasPriceIQ, request?.id, request?.year, setPrefill]);

  const pending = pendingPrefill(request?.id);

  const meta = [
    request?.product?.reference_number,
    request?.year,
    request?.condition,
  ]
    .flat()
    .filter(Boolean)
    .join(" / ");

  // Clicking a figure puts it in the field, saved like a typed value.
  const use = (field, minor) => onSave({ [field]: String(minor) });

  const asideFor = (field) => {
    if (!recs) return null;

    if (field === "buy_budget") {
      const buys = recs.previous_buys ?? [];
      const iq = hasPriceIQ ? recs.buy_priceiq : null;

      return (
        <>
          {iq && (
            <Rec label="Buy PriceIQ">
              <Chip
                className="rlb-rec__chip"
                onClick={() => use(field, iq.value)}
                title="Use this amount"
              >
                <Money value={iq.value} currency={recCurrency} decimals={0} size="sm" />
              </Chip>
            </Rec>
          )}
          {buys.length > 0 && (
            <Rec label={buys.length === 1 ? "Previous buy" : "Previous buys"}>
              {buys.map((b) => {
                const sub = [b.year, monthYear(b.date)].filter(Boolean).join(" · ");

                return (
                  <Chip
                    key={b.listing_id}
                    className="rlb-rec__chip"
                    onClick={() => use(field, b.price)}
                    title="Use this amount"
                  >
                    <Money value={b.price} currency={recCurrency} decimals={0} size="sm" />
                    {sub && <span className="rlb-rec__sub">{sub}</span>}
                  </Chip>
                );
              })}
            </Rec>
          )}
        </>
      );
    }

    if (field === "max_budget") {
      const listings = recs.listings ?? [];

      return (
        <>
          {listings.length > 1 && (
            <Rec label="Listings">
              {listings.map((l) => (
                <Chip
                  key={l.listing_id}
                  className="rlb-rec__chip"
                  onClick={() => use(field, l.price)}
                  title="Use this amount"
                >
                  <Money value={l.price} currency={recCurrency} decimals={0} size="sm" />
                  {l.year && <span className="rlb-rec__sub">{l.year}</span>}
                </Chip>
              ))}
            </Rec>
          )}
          <Rec label="Market median">
            {recs.market_median ? (
              <span className="rlb-rec__figure">
                <Money value={recs.market_median.value} currency={recCurrency} decimals={0} size="sm" />
                <span className="rlb-rec__sub">
                  {recs.market_median.window === "all_time"
                    ? "all time"
                    : recs.market_median.window.toUpperCase()}
                  {request?.year && !recs.market_median.year_filtered ? " · all years" : ""}
                </span>
              </span>
            ) : (
              <span className="rlb-rec__sub">No market data</span>
            )}
          </Rec>
        </>
      );
    }

    return null;
  };

  return (
    <li className="rlb-amount-card">
      <div className="rlb-amount-card__photo">
        {request?.product?.images?.[0]?.id ? (
          <img src={getFileUrl(request.product.images[0].id)} alt="" />
        ) : (
          <span className="rlb-amount-card__photo-empty" aria-hidden="true">
            <Icon name="Image" size="sm" />
          </span>
        )}
        <span className="rlb-amount-card__position">{position}</span>
      </div>

      <div className="rlb-amount-card__body">
        <div className="rlb-amount-card__head">
          <span className="rlb-amount-card__brand">
            {request?.product?.brand?.name}
          </span>
          <span className="rlb-amount-card__model">
            {request?.product?.model?.name}
          </span>
          {meta && <span className="rlb-amount-card__meta">{meta}</span>}
        </div>

        <div className="rlb-amount-card__fields">
          {fields.map(({ key, field, label }) => {
            const suggested = isEmptyAmount(request?.[field]) && !!pending[field];

            return (
              <div className="rlb-amount-row" key={key}>
                <AmountField
                  label={label}
                  value={suggested ? pending[field] : request?.[field]}
                  suggested={suggested}
                  currency={request?.currency}
                  currencies={currencies}
                  disabled={saveState === "saving"}
                  onCommit={({ amount, currency }) =>
                    onSave(
                      currency !== undefined
                        ? { currency }
                        : { [field]: amount }
                    )
                  }
                />
                <div className="rlb-amount-row__aside">{asideFor(field)}</div>
              </div>
            );
          })}
        </div>

        <span
          className={`rlb-amount-card__state rlb-amount-card__state--${saveState ?? "idle"}`}
          role="status"
        >
          {saveState === "saving" && "Saving…"}
          {saveState === "saved" && "Saved"}
          {saveState === "error" && "Couldn’t save — check the value and try again"}
        </span>
      </div>
    </li>
  );
};

export default RequestAmountCard;
